import React from 'react';

/*
 * The trail (DESIGN.md → Layout): a dashed route line runs down the left edge of
 * the content column. TrailStart is where it begins; each Section is a waypoint on
 * it. Consecutive trail elements join up because they carry no vertical margin.
 */

const TRAIL_LINE = 'absolute left-[5px] w-0 border-l-2 border-dashed border-contour';
const TRAIL_PAD = 'relative pl-8 md:pl-12';

export const TrailStart: React.FC<{ label: string }> = ({ label }) => (
    <div className="page-container">
        <div className={`${TRAIL_PAD} pt-6 pb-10`}>
            <span className={`${TRAIL_LINE} top-[2.15rem] bottom-0`} aria-hidden />
            <span className="absolute left-0 top-[1.85rem] w-3 h-3 rounded-full bg-gentian" aria-hidden />
            <p className="label m-0">{label}</p>
        </div>
    </div>
);

interface WaypointProps {
    /** Left column, next to the waypoint marker (a heading, a date …). */
    marker: React.ReactNode;
    id?: string;
    as?: 'section' | 'article';
    children: React.ReactNode;
}

/** One stop on the trail: a ring on the route line, a 12rem left column, content beside it. */
export const Waypoint: React.FC<WaypointProps> = ({ marker, id, as: Tag = 'section', children }) => (
    <Tag id={id} className="page-container block">
        <div className={`${TRAIL_PAD} pb-16 md:pb-20 grid gap-5 md:grid-cols-[12rem_1fr] md:gap-12`}>
            <span className={`${TRAIL_LINE} top-0 bottom-0`} aria-hidden />
            <span className="absolute left-0 top-[0.6rem] md:top-[0.75rem] w-3 h-3 rounded-full border-2 border-loden bg-paper" aria-hidden />
            <div className="flex flex-col gap-2">{marker}</div>
            <div className="min-w-0">{children}</div>
        </div>
    </Tag>
);

interface SectionProps {
    title: string;
    id?: string;
    /** Optional element under the heading, e.g. an "All projects" link. */
    aside?: React.ReactNode;
    children: React.ReactNode;
}

export const Section: React.FC<SectionProps> = ({ title, id, aside, children }) => (
    <Waypoint
        id={id}
        marker={<><h2 className="text-2xl md:text-[1.75rem] m-0">{title}</h2>{aside}</>}
    >
        {children}
    </Waypoint>
);

/** Closes the trail after the last section with a small map peak symbol. */
export const TrailEnd: React.FC = () => (
    <div className="page-container" aria-hidden>
        <div className={`${TRAIL_PAD} h-14`}>
            <span className={`${TRAIL_LINE} top-0 h-8`} />
            <svg className="absolute left-0 top-8 w-3 h-3 fill-ink" viewBox="0 0 12 12">
                <path d="M0 11 L12 11 L6 1 Z" />
            </svg>
        </div>
    </div>
);
